import { dehydrate } from '@tanstack/react-query';
import { GetServerSidePropsContext, GetServerSidePropsResult } from 'next';

import { VirtualCategory } from '@api/catalog';
import { getCategoryDetail, getVirtualCategoryDetail } from '@api/catalog/categories/api';
import { getProductFilters, getProducts, getProductsAuth } from '@api/catalog/product/api';
import { getInStockFilter } from '@api/catalog/product/catalogSearchParams';
import { getPagination } from '@api/common/helpers';
import { CommonResponse } from '@api/common/types';
import { getSeoTags } from '@api/seo/api';

import { HttpCode } from '@scripts/constants';
import { fromRoubleToKopecks, getSeoFilter, removeDefaultPaginationFromUrl, safeParseFilters } from '@scripts/helpers';
import { mergeProductsFilterResponses, splitLastFilter } from '@scripts/helpers/facets';
import { getCommonProps } from '@scripts/server/getCommonProps';
import { ExtendedSSRDataProps } from '@scripts/server/types';

import SubCategory from '../[category]/components/SubCategory';

const VirtualCategoryDetailPage = ({
    categoryId,
    seoData,
    dataProducts,
    total,
    categoryVirtualDetailData,
    categoriesVirtualDetailData,
    filters,
    isNotFound,
    isError = false,
}: ExtendedSSRDataProps) => {
    return (
        <SubCategory
            isVirtual
            categoryId={categoryId}
            seoData={seoData}
            dataProducts={dataProducts}
            totalSSR={total}
            categoryVirtualDetailData={categoryVirtualDetailData}
            categoriesVirtualDetailData={categoriesVirtualDetailData}
            filters={filters}
            isNotFound={isNotFound}
            isError={isError}
        />
    );
};

const ITEMS_PER_PRODUCTS_PAGE = 24;

export const getServerSideProps = async (
    context: GetServerSidePropsContext
): Promise<GetServerSidePropsResult<ExtendedSSRDataProps>> =>
    getCommonProps(context, async (props, queryClient, apiClient) => {
        const { params, query } = context;

        const isAuthUser = !!props.accessToken;

        const pageProps: ExtendedSSRDataProps = {
            isLeafCategory: true,
            categoryId: null,
            dehydratedState: null,
        };

        const urlCorrect = removeDefaultPaginationFromUrl('/catalog' + (context.req.url as string).split('catalog')[1]);

        try {
            const urlFilters = { ...context.query };

            if (urlFilters && Number(urlFilters.page) < 0) {
                context.res.statusCode = HttpCode.NOT_FOUND;

                return { props: { ...props, isNotFound: true } };
            }

            if (query && query.page && query.page.includes('page')) {
                context.res.statusCode = HttpCode.NOT_FOUND;

                return { props: { ...props, isNotFound: true } };
            }

            if ((query && Number(query.page) === 0) || (query && Number(query.page) === 1)) {
                return {
                    redirect: {
                        statusCode: 301,
                        destination: urlCorrect,
                    },
                };
            }

            delete urlFilters.code;
            delete urlFilters.page;

            const parsedFilters = safeParseFilters(urlFilters?.filters);

            const price_gte = parsedFilters?.price ? fromRoubleToKopecks(parsedFilters.price[0]) : null;
            const price_lte = parsedFilters?.price ? fromRoubleToKopecks(parsedFilters.price[1]) : null;

            const { price, isPreOrder, is_profitable, is_hit, ...restFilters } = parsedFilters;

            const preparedFilters = {
                ...restFilters,
                ...(price_gte && { price_gte }),
                ...(price_lte && { price_lte }),
                ...(isPreOrder === false && getInStockFilter()),
                ...(is_profitable === true && { is_profitable: true }),
                ...(is_hit === true && { is_hit: true }),
            };
            const { withoutLast: preparedFiltersWithoutLast, lastKey: lastFilterKey } =
                splitLastFilter(preparedFilters);
            const code = params?.code ? String(params.code) : String('');

            const { key: categoryVirtualDetailKey, fetch: categoryVirtualDetailFetch } = getVirtualCategoryDetail(
                apiClient
            )({
                filter: { code },
                include: [
                    'children',
                    'children.properties',
                    'children.property_values',
                    'properties',
                    'property_values',
                ],
            });

            try {
                const categoryVirtualDetailData: CommonResponse<VirtualCategory> = await queryClient.fetchQuery({
                    queryKey: categoryVirtualDetailKey,
                    queryFn: categoryVirtualDetailFetch,
                });

                const { key: categoriesVirtualDetailKey, fetch: categoriesVirtualDetailFetch } = getCategoryDetail(
                    apiClient
                )({
                    id: Number(
                        categoryVirtualDetailData?.data?.parent_virtual_category?.parent_pim_category_id ||
                            categoryVirtualDetailData?.data?.parent_pim_category_id
                    ),
                    offers_filter: preparedFilters,
                });

                const categoriesVirtualDetailData: CommonResponse<VirtualCategory> = await queryClient.fetchQuery({
                    queryKey: categoriesVirtualDetailKey,
                    queryFn: categoriesVirtualDetailFetch,
                });

                if (!categoryVirtualDetailData.data) {
                    context.res.statusCode = HttpCode.NOT_FOUND;

                    return { props: { ...props, isNotFound: true } };
                }

                const { key: baseProductsCategoryKey, fetch: baseProductsCategoryFetch } = getProducts(apiClient)({
                    sort: 'availability_order',
                    filter: {
                        ...(categoryVirtualDetailData.data.id !== null && {
                            virtual_category_id: Number(categoryVirtualDetailData.data.id),
                        }),
                        ...preparedFilters,
                    },
                    pagination: getPagination(ITEMS_PER_PRODUCTS_PAGE, Number(context.query.page)),
                    include: ['brand', 'category', 'images', 'attributes', 'seller'],
                });

                const { key: baseProductsCategoryAuthKey, fetch: baseProductsCategoryAuthFetch } = getProductsAuth(
                    apiClient
                )({
                    sort: 'availability_order',
                    filter: {
                        ...(categoryVirtualDetailData.data.id !== null && {
                            virtual_category_id: Number(categoryVirtualDetailData.data.id),
                        }),
                        ...preparedFilters,
                    },
                    pagination: getPagination(ITEMS_PER_PRODUCTS_PAGE, Number(context.query.page)),
                    include: ['brand', 'category', 'images', 'attributes', 'seller'],
                });

                const dataProducts = await queryClient.fetchQuery({
                    queryKey: isAuthUser ? baseProductsCategoryAuthKey : baseProductsCategoryKey,
                    queryFn: isAuthUser ? baseProductsCategoryAuthFetch : baseProductsCategoryFetch,
                });

                const paginationPage = dataProducts?.meta?.pagination;

                if (
                    paginationPage &&
                    query.page &&
                    +query.page > Math.ceil(+paginationPage.total / +paginationPage.limit)
                ) {
                    context.res.statusCode = HttpCode.NOT_FOUND;

                    return { props: { ...props, isNotFound: true } };
                }

                const { key: seoTagsKey, fetch: baseSeoTagsFetch } = getSeoTags(apiClient)(
                    getSeoFilter(String(urlCorrect), categoryVirtualDetailData.data.id)
                );

                const { key: filtersKey, fetch: filtersFetch } = getProductFilters(apiClient)({
                    filter: {
                        ...(categoryVirtualDetailData.data.id !== null && {
                            virtual_category_id: Number(categoryVirtualDetailData.data.id),
                        }),
                        ...preparedFilters,
                    },
                });

                const { key: filtersLimitedKey, fetch: filtersLimitedFetch } = getProductFilters(apiClient)({
                    filter: {
                        ...(categoryVirtualDetailData.data.id !== null && {
                            virtual_category_id: Number(categoryVirtualDetailData.data.id),
                        }),
                        ...preparedFiltersWithoutLast,
                    },
                });

                const { key: filtersBaseKey, fetch: filtersBaseFetch } = getProductFilters(apiClient)({
                    filter: {
                        ...(categoryVirtualDetailData.data.id !== null && {
                            virtual_category_id: Number(categoryVirtualDetailData.data.id),
                        }),
                    },
                });

                const [filtersResponse, filtersLimitedResponse, filtersBaseResponse] = await Promise.all([
                    queryClient.fetchQuery({
                        queryKey: filtersKey,
                        queryFn: filtersFetch,
                    }),
                    queryClient.fetchQuery({
                        queryKey: filtersLimitedKey,
                        queryFn: filtersLimitedFetch,
                    }),
                    queryClient.fetchQuery({
                        queryKey: filtersBaseKey,
                        queryFn: filtersBaseFetch,
                    }),
                ]);

                const filters = mergeProductsFilterResponses(
                    filtersBaseResponse.data,
                    filtersResponse.data,
                    filtersLimitedResponse.data,
                    lastFilterKey ? String(lastFilterKey) : undefined
                );

                const seoData = await queryClient.fetchQuery({
                    queryKey: seoTagsKey,
                    queryFn: baseSeoTagsFetch,
                });

                pageProps.dehydratedState = dehydrate(queryClient);
                pageProps.categoryId = categoryVirtualDetailData.data.id;

                return {
                    props: {
                        dataProducts,
                        total: dataProducts?.meta?.pagination?.total || 0,
                        seoData: {
                            data: {
                                ...seoData.data,
                                seo_text:
                                    (query && (Number(query.page) === 0 || Number(query.page) === 1)) ||
                                    (query && !query.page)
                                        ? seoData.data.seo_text
                                        : '',
                            },
                        },
                        categoryVirtualDetailData: categoryVirtualDetailData,
                        categoriesVirtualDetailData: categoriesVirtualDetailData,
                        filters,
                        ...props,
                        ...pageProps,
                    },
                };
            } catch (error) {
                context.res.statusCode = HttpCode.NOT_FOUND;

                return {
                    props: {
                        ...props,
                        isNotFound: true,
                    },
                };
            }
        } catch (error) {
            context.res.statusCode = HttpCode.NOT_FOUND;

            return {
                props: {
                    ...props,
                    isNotFound: true,
                },
            };
        }
    });

export default VirtualCategoryDetailPage;
